// mui
import { Chip, ChipProps } from '@mui/material';

// models
import {
  BacktestRunStatus,
  EvaluationSourceStatus,
  MatchType,
  RunOutcome,
  TranscriptStatus,
} from '@karehero/models';

interface ChipValues {
  run: BacktestRunStatus;
  source: EvaluationSourceStatus;
  transcript: TranscriptStatus;
  outcome: RunOutcome;
  matchType: MatchType;
}

export type EvaluationChipKind = keyof ChipValues;

type ChipStyle = { label: string; color: ChipProps['color'] };

const CHIPS: { [K in EvaluationChipKind]: Record<ChipValues[K], ChipStyle> } = {
  run: {
    queued: { label: 'Queued', color: 'default' },
    transcribing: { label: 'Transcribing', color: 'info' },
    extracting: { label: 'Extracting', color: 'info' },
    comparing: { label: 'Comparing', color: 'info' },
    completed: { label: 'Completed', color: 'success' },
    failed: { label: 'Failed', color: 'error' },
  },
  source: {
    fetching: { label: 'Fetching', color: 'info' },
    ready: { label: 'Ready', color: 'success' },
    failed: { label: 'Failed', color: 'error' },
  },
  transcript: {
    transcribing: { label: 'Transcribing', color: 'info' },
    ready: { label: 'Ready', color: 'success' },
    failed: { label: 'Failed', color: 'error' },
  },
  outcome: {
    matched: { label: 'Matched', color: 'success' },
    mismatched: { label: 'Mismatched', color: 'error' },
    missing: { label: 'Missing', color: 'warning' },
    unexpected: { label: 'Unexpected', color: 'info' },
    not_judged: { label: 'Not judged', color: 'default' },
  },
  matchType: {
    exact: { label: 'Exact', color: 'default' },
    semantic: { label: 'Semantic', color: 'default' },
    absent: { label: 'Absent', color: 'default' },
  },
};

/** A value the API added since, shown as it came. */
const chipFor = <K extends EvaluationChipKind>(
  kind: K,
  value: ChipValues[K],
): ChipStyle =>
  CHIPS[kind][value] ?? { label: String(value), color: 'default' };

export const evaluationChipLabel = <K extends EvaluationChipKind>(
  kind: K,
  value: ChipValues[K],
) => chipFor(kind, value).label;

export interface EvaluationChipProps<K extends EvaluationChipKind> {
  kind: K;
  value: ChipValues[K];
}

export const EvaluationChip = <K extends EvaluationChipKind>({
  kind,
  value,
}: EvaluationChipProps<K>) => {
  const { label, color } = chipFor(kind, value);
  return (
    <Chip
      label={label}
      color={color}
      variant={kind === 'matchType' ? 'outlined' : 'filled'}
      size='small'
    />
  );
};

EvaluationChip.displayName = 'EvaluationChip';
export default EvaluationChip;
